import React, { useState } from 'react';
import { CheckCircle, Clock, ChevronDown, Circle, Archive } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'none', label: 'None', icon: Circle, color: 'text-gray-500' },
  { value: 'in_progress', label: 'In Progress', icon: Clock, color: 'text-orange-500' },
  { value: 'completed', label: 'Completed', icon: CheckCircle, color: 'text-green-500' },
  { value: 'archive', label: 'Archive', icon: Archive, color: 'text-gray-400' },
];

/* ─── "Change Status" orange outlined button with dropdown ─── */
export function ChangeStatusButton({ status, onStatusChange }) {
  const [open, setOpen] = useState(false);

  const handleSelect = (value) => {
    console.log("STATUS CHANGED", value);
    onStatusChange(value);
    setOpen(false);
  };

  // When status is in_progress, show "In Progress ∨" as the button
  if (status === 'in_progress') {
    return (
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-orange-500 border-2 border-orange-400 rounded-full hover:bg-orange-50 transition-colors"
          data-testid="change-status-btn"
        >
          <Clock className="w-4 h-4" />
          In Progress
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
        {open && <StatusDropdownMenu current={status} onSelect={handleSelect} onClose={() => setOpen(false)} />}
      </div>
    );
  }

  // Default and completed: show "Change Status" text
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-orange-500 border-2 border-orange-400 rounded-full hover:bg-orange-50 transition-colors"
        data-testid="change-status-btn"
      >
        Change Status
      </button>
      {open && <StatusDropdownMenu current={status} onSelect={handleSelect} onClose={() => setOpen(false)} />}
    </div>
  );
}

/* ─── "Mark Complete" green outlined button ─── */
export function MarkCompleteButton({ onMarkComplete }) {
  return (
    <button
      onClick={() => {
        console.log("CARD MARKED COMPLETE");
        onMarkComplete();
      }}
      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-green-600 border-2 border-green-500 rounded-full hover:bg-green-50 transition-colors"
      data-testid="mark-complete-btn"
    >
      Mark Complete
    </button>
  );
}

/* ─── Completed info display (green checkmark + user + date) ─── */
export function CompletedInfo({ completedByName, completedAt }) {
  const formattedDate = completedAt
    ? new Date(completedAt).toLocaleString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      }).replace(',', ' |')
    : '';

  return (
    <div className="flex items-center gap-2" data-testid="completed-info">
      <CheckCircle className="w-8 h-8 text-green-500" />
      <div>
        <p className="text-sm font-bold text-green-600">
          Completed by {completedByName || 'Unknown'}
        </p>
        <p className="text-xs text-green-500">
          On {formattedDate}
        </p>
      </div>
    </div>
  );
}

/* ─── Dropdown menu for status selection ─── */
function StatusDropdownMenu({ current, onSelect, onClose }) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-zinc-900 border border-border rounded-xl shadow-lg min-w-[180px] py-2" data-testid="status-dropdown-menu">
        {STATUS_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              onClick={() => onSelect(opt.value)}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-muted flex items-center gap-3 transition-colors ${
                opt.value === current ? 'font-bold bg-muted/50' : 'font-medium'
              }`}
              data-testid={`status-option-${opt.value}`}
            >
              <Icon className={`w-4 h-4 ${opt.color}`} />
              <span className={opt.color}>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ─── Short card status badge (used in board card front) ─── */
export function CardStatusBadge({ status, onStatusChange }) {
  const [open, setOpen] = useState(false);

  const handleSelect = (value) => {
    console.log("STATUS CHANGED", value);
    onStatusChange(value);
    setOpen(false);
  };

  if (!status || status === 'none') return null;

  if (status === 'in_progress') {
    return (
      <div className="relative mb-2">
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-300 rounded-full"
          data-testid="card-status-badge"
        >
          <Clock className="w-3 h-3" />
          In Progress
          <ChevronDown className="w-3 h-3" />
        </button>
        {open && (
          <div onClick={(e) => e.stopPropagation()}>
            <StatusDropdownMenu current={status} onSelect={handleSelect} onClose={() => setOpen(false)} />
          </div>
        )}
      </div>
    );
  }

  return null; // completed banner is handled separately in BoardPage
}
